import type { SortingState } from '@disco/types/types/data-table'
import type { FilterDefinition, FilterState } from 'types/filter-page'
import type {
  SearchPageState,
  SourceScope,
  V2SearchQuery,
} from 'types/search-page'

import {
  MAX_SEGMENT_LENGTH_SECONDS_AIMS,
  SOURCE_FILTER_FIELD,
  SOURCE_SCOPE,
} from '@disco/constants/search'

import { getStartDateISO, getEndDateISO } from './date'

export const DEFAULT_SOURCE_SCOPE: readonly SourceScope[] = ['business', 'mds']

const META_FILTER_FIELDS = new Set([
  'look_in_inboxes',
  'look_in_channels',
  SOURCE_FILTER_FIELD,
])

/**
 * Filters a list of filter definitions based on keyword search.
 *
 * Searches through filter labels and their options to find matches with the
 * provided keywords. Returns filtered results that include matching filters
 * and filters with matching options.
 *
 * @param keywords - Search keywords to filter by
 * @param filters - Array of filter definitions to search through
 * @returns Filtered array of filter definitions matching the keywords
 *
 * @example
 * ```typescript
 * const filters = [
 *   { label: 'Genre', options: [{ label: 'Rock' }, { label: 'Jazz' }] },
 *   { label: 'Duration', options: [{ label: 'Short' }, { label: 'Long' }] }
 * ]
 *
 * const filtered = filterFilters('rock', filters)
 * // Returns filters containing 'rock' in label or options
 *
 * const empty = filterFilters('', filters)
 * // Returns all filters when no keywords provided
 * ```
 */
export const filterFilters = (keywords: string, filters: FilterDefinition[]) =>
  keywords
    ? filters.reduce((acc: FilterDefinition[], filter) => {
        if (filter.label.toLowerCase().includes(keywords.toLowerCase())) {
          acc.push(filter)
        } else if (filter.options) {
          const options = filter.options.filter(option =>
            option.label.toLowerCase().includes(keywords.toLowerCase()),
          )
          if (options.length > 0) {
            acc.push({ ...filter, options })
          }
        }
        return acc
      }, [])
    : filters

/**
 * Extracts group IDs from filter state for use with the X-Disco-GroupIDs header.
 *
 * Searches through the filter state to find group filters and extracts their
 * group IDs for use in API calls via the X-Disco-GroupIDs header.
 *
 * @param state - Array of filter states to extract group IDs from
 * @returns Array of group IDs or undefined if no group filters found
 *
 * @example
 * ```typescript
 * const filterState = [
 *   {
 *     definition: { field: 'look_in_inboxes' },
 *     value: [{ id: 1 }, { id: 2 }]
 *   }
 * ]
 *
 * const groupIds = extractGroupIdsFromFilters(filterState)
 * // Returns: [1, 2]
 * ```
 */
export const extractGroupIdsFromFilters = (
  state: FilterState[],
): number[] | undefined => {
  const groupFilters = state.filter(
    filter =>
      (filter.definition.field === 'look_in_inboxes' ||
        filter.definition.field === 'look_in_channels') &&
      filter.value,
  )

  if (groupFilters.length === 0) {
    return undefined
  }

  const groupIds: number[] = []

  for (const filter of groupFilters) {
    if (!Array.isArray(filter.value)) continue
    const items: unknown[] = filter.value
    for (const group of items) {
      if (group === null || typeof group !== 'object') continue
      const id = Reflect.get(group, 'id')
      if (typeof id === 'number') {
        groupIds.push(id)
      }
    }
  }

  return groupIds.length > 0 ? groupIds : undefined
}

/**
 * Transforms FilterState[] to V2 API format.
 *
 * Converts UI filter state to the V2 API format, excluding group filters
 * which are handled separately via search_in.groups parameter.
 *
 * @param filters - Array of filter states from UI
 * @returns V2 API filter object or null if no active filters
 *
 * @example
 * ```typescript
 * const filters = [
 *   {
 *     definition: { field: 'title' },
 *     operator: 'contains',
 *     value: 'test'
 *   }
 * ]
 *
 * const v2Filters = transformFiltersToV2(filters)
 * // Returns: { operator: 'and', filters: [{ field: 'title', operator: 'contains', value: 'test' }] }
 * ```
 */
export const transformFiltersToV2 = (filters: FilterState[]) => {
  const activeFilters = filters.filter(filter => {
    if (filter.value === undefined || filter.value === null) return false
    if (META_FILTER_FIELDS.has(filter.definition.field)) {
      return false
    }
    return true
  })

  if (activeFilters.length === 0) return null

  // Build API payload directly from FilterState
  return {
    operator: 'and' as const,
    filters: activeFilters.map(filter => {
      let transformedValue = filter.value

      // Transform date values to ISO string format
      if (
        filter.definition.type === 'date' ||
        filter.definition.type === 'partial_date'
      ) {
        if (Array.isArray(filter.value) && filter.value.length === 2) {
          // Handle date ranges - convert to [startISO, endISO]
          transformedValue = [
            getStartDateISO(filter.value[0]),
            getEndDateISO(filter.value[1]),
          ]
        } else if (typeof filter.value === 'string') {
          // Handle single date - convert to start of day ISO
          transformedValue = getStartDateISO(filter.value)
        }
      }

      return {
        field: filter.definition.field,
        operator: filter.operator || 'equals',
        value: transformedValue,
      }
    }),
  }
}

/**
 * Maps UI column IDs to their corresponding API sort field names.
 * The backend uses different field names than the UI in some cases.
 */
const SORT_FIELD_MAP: Record<string, string> = {
  title: 'name',
  source: 'scope',
}

/**
 * Builds sort array for V2 API with dynamic defaults.
 * Uses score for searches, created date otherwise.
 */
export const dynamicSort = (
  sorting: SortingState | undefined,
  hasActiveSearch: boolean,
): string[] => {
  if (sorting && sorting.length > 0) {
    // User has selected custom sorting - use it directly
    return sorting.map(s => {
      const field = s.id.toLowerCase()
      const mappedField = SORT_FIELD_MAP[field] ?? field
      return s.desc ? `-${mappedField}` : mappedField
    })
  }

  // No custom sorting - use dynamic default
  if (hasActiveSearch) {
    // With query/filters: sort by score (relevance)
    return ['-score']
  } else {
    // Without query/filters: sort by created date (newest first)
    return ['-created']
  }
}

/**
 * Maps UI search types to API search types
 * @param searchType The search type from the UI dropdown
 * @returns The corresponding API search type
 */
export const getApiSearchType = (
  searchType: string | null | undefined,
): 'regular' | 'simsearch' | 'llm' => {
  switch (searchType) {
    case 'keyword':
      return 'regular'
    case 'similarity':
      return 'simsearch'
    case 'prompt':
      return 'llm'
    default:
      return 'regular'
  }
}

/**
 * Clean a URL query by extracting just the URL part (first whitespace-separated token).
 * This handles cases where users paste a URL followed by extra text.
 * Only applies to similarity searches where the query is expected to be a URL.
 *
 * @param query The raw query string
 * @param searchType The search type
 * @returns Cleaned query string
 */
export const cleanUrlQuery = (query: string, searchType: string): string => {
  if (!query) return query

  // Only clean for similarity search (URL-based searches)
  if (searchType !== 'similarity') {
    return query
  }

  // Check if the query looks like a URL
  const trimmed = query.trim()
  const looksLikeUrl =
    trimmed.startsWith('http://') ||
    trimmed.startsWith('https://') ||
    trimmed.includes('spotify.com') ||
    trimmed.includes('youtube.com') ||
    trimmed.includes('youtu.be') ||
    trimmed.includes('music.apple.com') ||
    trimmed.includes('itunes.apple.com') ||
    trimmed.includes('disco.ac') ||
    trimmed.includes('s.disco')

  if (!looksLikeUrl) {
    return query
  }

  // Extract just the URL part (first whitespace-separated token)
  return trimmed.split(/\s+/)[0]
}

// A non-empty search query, ignoring filters.
export const hasActiveSearchQuery = (
  query: string | null | undefined,
  searchType: string | null | undefined,
): boolean => Boolean(cleanUrlQuery(query || '', searchType ?? '').trim())

// At least one non-meta filter carries a value (source scope is not a filter).
export const hasActiveFilters = (
  filters: FilterState[] | null | undefined,
): boolean =>
  Boolean(
    filters &&
      filters.some(
        f =>
          f.value !== undefined &&
          f.value !== null &&
          !META_FILTER_FIELDS.has(f.definition?.field),
      ),
  )

export const getHasActiveSearch = (
  query: string | null | undefined,
  filters: FilterState[] | null | undefined,
  searchType: string | null | undefined,
): boolean =>
  hasActiveSearchQuery(query, searchType) || hasActiveFilters(filters)

/**
 * Convert V2 group IDs to URL strings to match expected format. Returns a
 * shallow copy of `track` with a normalised `groups` value; the input object
 * is left untouched so the function behaves as a pure transform. Array inputs
 * are normalised to `string[]`; non-array truthy values are passed through
 * unchanged so callers can detect and handle the malformed shape, hence the
 * `groups: unknown` return shape.
 */
export const normalizeV2Track = <T extends { groups?: unknown }>(
  track: T,
): Omit<T, 'groups'> & { groups: unknown } => {
  if (Array.isArray(track.groups)) {
    const groupArr: unknown[] = track.groups
    const nextGroups: string[] = groupArr
      .map(group => {
        // If group is already a string URL, keep it
        if (typeof group === 'string') {
          return group
        }
        // If group is a number (V2 API format), convert to URL string
        if (typeof group === 'number') {
          return `${window.location.protocol}//${window.location.host}/api/group/${group}/`
        }
        // If group is an object with an ID, convert to URL string. Use an
        // explicit null/undefined check so a numeric `0` id is preserved.
        if (typeof group === 'object' && group !== null) {
          const id = Reflect.get(group, 'id')
          if (typeof id === 'number' || (typeof id === 'string' && id !== '')) {
            return `${window.location.protocol}//${window.location.host}/api/group/${id}/`
          }
        }
        // For any other format, skip to avoid issues
        return null
      })
      .filter((g): g is string => g !== null)
    return { ...track, groups: nextGroups }
  }
  if (!track.groups) {
    return { ...track, groups: [] as string[] }
  }
  // Non-array, truthy values (e.g. an unexpected string payload) are passed
  // through unchanged so callers can detect and handle the malformed shape.
  return { ...track, groups: track.groups }
}

// True when the popover is untouched (`null`) or explicitly set to both scopes,
// i.e. the user has not narrowed away from the widest MDS-enabled default.
export const isUntouchedOrBothScopes = (
  sourceScope?: SourceScope[] | null,
): boolean => {
  if (!sourceScope) return true
  const unique = new Set(sourceScope)
  return (
    unique.size === DEFAULT_SOURCE_SCOPE.length &&
    DEFAULT_SOURCE_SCOPE.every(scope => unique.has(scope))
  )
}

export const buildV2SearchQuery = (
  state: SearchPageState,
  cursor?: string,
  mdsSearchEnabled: boolean = false,
  aimsEnabled: boolean = false,
): V2SearchQuery => {
  const groupIdsFromFilters = extractGroupIdsFromFilters(state.filters) || []
  const cleanedQuery = cleanUrlQuery(state.query || '', state.searchType)

  const hasActiveSearch = getHasActiveSearch(
    state.query,
    state.filters,
    state.searchType,
  )

  const sort = dynamicSort(state.sorting, hasActiveSearch)

  // Widen to MDS once the user is actually searching (query or active filters);
  // a bare browse stays business-only. An explicit popover choice overrides both.
  const scope = buildV2SearchScope(
    mdsSearchEnabled,
    state.sourceScope,
    hasActiveSearch,
  )

  const v2Query: V2SearchQuery = {
    query: cleanedQuery,
    size: 50,
    type: getApiSearchType(state.searchType),
    scope,
    sort,
    ...(cursor && { cursor }),
    ...(state.searchType === 'similarity' &&
      state.appliedSegmentStart !== null &&
      state.appliedSegmentEnd !== null &&
      state.appliedSegmentStart >= 1 &&
      state.appliedSegmentEnd > state.appliedSegmentStart && {
        time_offset: state.appliedSegmentStart,
        // For AIMS: clamp to max allowed segment length
        // For our sim search: clamp to track duration as a defensive guard
        time_limit: aimsEnabled
          ? Math.min(
              state.appliedSegmentEnd - state.appliedSegmentStart,
              MAX_SEGMENT_LENGTH_SECONDS_AIMS,
            )
          : Math.min(
              state.appliedSegmentEnd - state.appliedSegmentStart,
              state.linkInfo?.duration ?? Infinity,
            ),
      }),
    ...(groupIdsFromFilters.length > 0 && {
      search_in: {
        groups: groupIdsFromFilters,
        groups_descendants: true,
        include_playlist_tracks: true,
      },
    }),
  }

  const v2Filters = transformFiltersToV2(state.filters)
  if (v2Filters) {
    v2Query.filters = v2Filters
  }

  return v2Query
}

// Single source of truth for the source scope, shared by the query builder and
// the source popover UI so the checkboxes always mirror the searched scope. An
// explicit popover choice wins; otherwise widen to both scopes once a search is
// active (query or filters), else stay business-only.
export const resolveSourceScope = (
  userChoice: SourceScope[] | null | undefined,
  hasActiveSearch: boolean,
): SourceScope[] => {
  if (userChoice && userChoice.length > 0) {
    return [...userChoice]
  }
  return hasActiveSearch
    ? [SOURCE_SCOPE.BUSINESS, SOURCE_SCOPE.MDS]
    : [SOURCE_SCOPE.BUSINESS]
}

// MDS disabled forces business-only regardless of choice; otherwise defer to the
// shared resolver.
export const buildV2SearchScope = (
  mdsSearchEnabled: boolean,
  userChoice: SourceScope[] | null | undefined,
  hasActiveSearch: boolean,
): SourceScope[] => {
  if (!mdsSearchEnabled) {
    return [SOURCE_SCOPE.BUSINESS]
  }
  return resolveSourceScope(userChoice, hasActiveSearch)
}
